import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";

import type { CurrentUser } from "@thalamos/haven-common";

import api, { apiHooks } from "../api/index.js";
import { setUser } from "../tracing.js";

/**
 * How often to ask the server whether the session is still alive. Any other
 * authenticated call extends the session; `signedInCheck` does not, so this
 * poll is what turns a server-side idle timeout into the user being signed out
 * of the tab rather than discovering it on their next action.
 */
const SIGNED_IN_CHECK_INTERVAL_MS = 10_000;

export type UserContextType = {
  user: CurrentUser | undefined;
  loading: boolean;
  error: Error | null;
  refetchUser: () => Promise<unknown>;
};

export const UserContext = createContext<UserContextType | undefined>(
  undefined,
);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [searchParams] = useSearchParams();

  // An emailed link names the organisation and kind of work it was issued for,
  // and the server resolves the session against it as the user is loaded.
  const sessionContextOrganisationId =
    searchParams.get("session-context-organisation-id") ?? undefined;
  const sessionContextKind =
    searchParams.get("session-context-kind") ?? undefined;

  const [{ data, loading, response, error }, refetchUser] = apiHooks.user.me(
    sessionContextOrganisationId,
    sessionContextKind,
  );

  // A 401 is handled by the axios interceptor, which navigates away, so the
  // only case left here is a 200 whose body did not parse.
  const user = response?.status === 200 ? data : undefined;

  useEffect(() => {
    setUser(user ?? null);
  }, [user]);

  useEffect(() => {
    if (!user) {
      return undefined;
    }

    const interval = setInterval(() => {
      void api.user.signedInCheck();
    }, SIGNED_IN_CHECK_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [user]);

  return (
    <UserContext.Provider value={{ user, loading, error, refetchUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
